import { useState, useEffect, useCallback, useRef } from "react";
import { X, ChevronLeft, ChevronRight, Download } from "lucide-react";
import {
  Dialog,
  DialogPortal,
  DialogOverlay,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { cn } from "@/lib/utils";

export interface ImageGalleryFile {
  id: string;
  url: string;
  name: string;
}

export interface ImageGalleryProps {
  /** List of image files to display. Each must have id, url, and name. */
  files: ImageGalleryFile[];
  /** Index of the image to show when open; null = gallery closed. */
  openIndex: number | null;
  onClose: () => void;
  /** Optional aria-label for the dialog. */
  ariaLabel?: string;
}

const SWIPE_THRESHOLD = 50;

const actionButtonClass =
  "inline-flex items-center justify-center border border-white/50 text-white hover:border-white hover:bg-white/10 rounded-none px-3 py-2 transition-colors duration-300 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-inset disabled:opacity-50";

export default function ImageGallery({
  files,
  openIndex,
  onClose,
  ariaLabel = "Galeria de imagem",
}: ImageGalleryProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isRtl, setIsRtl] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const dragStartX = useRef<number | null>(null);
  const dragStartY = useRef<number | null>(null);

  const isOpen = openIndex !== null && files.length > 0;

  const goPrev = useCallback(() => {
    setCurrentIndex((i) => (i <= 0 ? files.length - 1 : i - 1));
  }, [files.length]);
  const goNext = useCallback(() => {
    setCurrentIndex((i) => (i >= files.length - 1 ? 0 : i + 1));
  }, [files.length]);

  const handleSwipeEnd = useCallback(
    (deltaX: number) => {
      if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;
      const rtl = typeof document !== "undefined" && document.documentElement.dir === "rtl";
      if (deltaX > 0) {
        if (rtl) goNext();
        else goPrev();
      } else {
        if (rtl) goPrev();
        else goNext();
      }
    },
    [goPrev, goNext]
  );

  useEffect(() => {
    if (openIndex !== null && openIndex >= 0 && openIndex < files.length) {
      setCurrentIndex(openIndex);
    }
  }, [openIndex, files.length]);

  useEffect(() => {
    if (!isOpen) return;
    setIsRtl(typeof document !== "undefined" && document.documentElement.dir === "rtl");

    const handleKey = (e: KeyboardEvent) => {
      const rtl = document.documentElement.dir === "rtl";
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          if (rtl) goNext();
          else goPrev();
          return;
        }
        if (e.key === "ArrowRight") {
          e.preventDefault();
          if (rtl) goPrev();
          else goNext();
        }
    };
    const handleMouseUp = (e: MouseEvent) => {
      if (dragStartX.current === null) return;
      const delta = e.clientX - dragStartX.current;
      dragStartX.current = null;
      handleSwipeEnd(delta);
    };
    const handleTouchMove = (e: TouchEvent) => {
      if (dragStartX.current === null || dragStartY.current === null) return;
      const dx = Math.abs(e.touches[0].clientX - dragStartX.current);
      const dy = Math.abs(e.touches[0].clientY - (dragStartY.current ?? 0));
      if (dx > 10 && dx > dy) e.preventDefault();
    };

    document.addEventListener("keydown", handleKey, true);
    document.addEventListener("mouseup", handleMouseUp);
    const overlay = overlayRef.current;
    if (overlay) {
      overlay.addEventListener("touchmove", handleTouchMove, { passive: false });
    }
    return () => {
      document.removeEventListener("keydown", handleKey, true);
      document.removeEventListener("mouseup", handleMouseUp);
      if (overlay) overlay.removeEventListener("touchmove", handleTouchMove);
      dragStartX.current = null;
      dragStartY.current = null;
    };
  }, [isOpen, files.length, goPrev, goNext, handleSwipeEnd]);

  if (!isOpen) return null;

  const current = files[currentIndex];
  if (!current) return null;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          ref={overlayRef}
          aria-label={ariaLabel}
          className={cn(
            "fixed inset-0 z-50 flex items-center justify-center p-4 cursor-pointer",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "duration-300"
          )}
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
          onTouchStart={(e) => {
            dragStartX.current = e.touches[0].clientX;
            dragStartY.current = e.touches[0].clientY;
          }}
          onTouchEnd={(e) => {
            if (dragStartX.current === null) return;
            const delta = e.changedTouches[0].clientX - dragStartX.current;
            dragStartX.current = null;
            dragStartY.current = null;
            handleSwipeEnd(delta);
          }}
          onMouseDown={(e) => {
            dragStartX.current = e.clientX;
          }}
        >
          <VisuallyHidden.Root asChild>
            <DialogPrimitive.Title>
              {ariaLabel}
            </DialogPrimitive.Title>
          </VisuallyHidden.Root>
          <VisuallyHidden.Root asChild>
            <DialogPrimitive.Description>
              {current.name} — {currentIndex + 1} de {files.length}
            </DialogPrimitive.Description>
          </VisuallyHidden.Root>

          {/* Invisible navigation zones */}
          <button
            type="button"
            className="absolute left-0 top-0 bottom-0 w-1/4 min-w-[80px] max-w-[25vw] z-10 cursor-pointer touch-none"
            aria-label={isRtl ? "Próxima imagem" : "Imagem anterior"}
            onClick={(e) => {
              e.stopPropagation();
              if (isRtl) goNext();
              else goPrev();
            }}
          />
          <button
            type="button"
            className="absolute right-0 top-0 bottom-0 w-1/4 min-w-[80px] max-w-[25vw] z-10 cursor-pointer touch-none"
            aria-label={isRtl ? "Imagem anterior" : "Próxima imagem"}
            onClick={(e) => {
              e.stopPropagation();
              if (isRtl) goPrev();
              else goNext();
            }}
          />

          {/* Top-right action buttons */}
          <div className="absolute top-4 right-4 flex items-center gap-2 z-20">
            <a
              href={current.url}
              download={current.name}
              target="_blank"
              rel="noopener noreferrer"
              className={actionButtonClass}
              aria-label="Descarregar"
              onClick={(e) => e.stopPropagation()}
            >
              <Download className="h-5 w-5" strokeWidth={1.5} />
            </a>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-white hover:text-white hover:bg-white/10 border border-white/50 hover:border-white [&_svg]:size-5"
              aria-label="Fechar"
            >
              <X strokeWidth={1.5} />
            </Button>
          </div>

          {/* Prev chevron */}
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-primary-200 disabled:opacity-50 z-20 [&_svg]:size-8"
            aria-label="Imagem anterior"
          >
            <ChevronLeft strokeWidth={1.5} />
          </Button>

          {/* Image */}
          <div
            className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center cursor-default z-20"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={current.url}
              alt={current.name}
              className="max-w-full max-h-[90vh] w-auto h-auto object-contain"
            />
          </div>

          {/* Next chevron */}
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-primary-200 disabled:opacity-50 z-20 [&_svg]:size-8"
            aria-label="Próxima imagem"
          >
            <ChevronRight strokeWidth={1.5} />
          </Button>

          {/* Counter */}
          <span className="absolute bottom-4 left-1/2 -translate-x-1/2 text-sm text-white/90 z-20">
            {currentIndex + 1} / {files.length}
          </span>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
